import React, { forwardRef, useState, useEffect } from "react";
import "../../styles/atoms.scss";
import SvgIcn from "../../data/IconCompo";

const Switchz = forwardRef(
  (
    {
      onText = "",
      offText = "",
      className = "",
      hintText = "",
      hintIcon = "",
      width = "80px",
      onIcon = "",
      offIcon = "",
      direction = "row",
      isDefaultOn = false,
      onToggle = () => {},
      hintClassName = "",
      textClassName = "",
      circleClassName = "",
      name,
      error,
      value = undefined,
      disabled = false,
      id,
      ariaLabel,
      transitionDuration = "0.3s",
    },
    ref
  ) => {
    // Use internal state if value prop is undefined
    const [internalValue, setInternalValue] = useState(isDefaultOn);
    const isControlled = value !== undefined;
    const isOn = isControlled ? value : internalValue;

    // Sync with isDefaultOn when it changes
    useEffect(() => {
      if (!isControlled) {
        setInternalValue(isDefaultOn);
      }
    }, [isDefaultOn, isControlled]);

    const handleToggle = (event) => {
      if (disabled) return;

      // Handle keyboard events
      if (event.type === "keydown") {
        if (event.key !== "Enter" && event.key !== " ") {
          return;
        }
        event.preventDefault();
      }

      // Update internal state if uncontrolled
      if (!isControlled) {
        setInternalValue(!internalValue);
      }

      // Call onToggle callback with updated value
      if (typeof onToggle === "function") {
        onToggle({ name, value: !isOn });
      }
    };

    const switchId = id || `switch-${Math.random().toString(36).substr(2, 9)}`;
    const ariaLabelText =
      ariaLabel || hintText || (isOn ? onText : offText) || "Toggle switch";

    return (
      <div
        className={`switch-container ${className}`}
        style={{ flexDirection: direction }}
        role="presentation"
      >
        {hintText && (
          <div className={`hint ${hintClassName}`} id={`${switchId}-hint`}>
            {hintIcon && <SvgIcn name={hintIcon} aria-hidden="true" />}
            <span>{hintText}</span>
          </div>
        )}

        <div
          ref={ref}
          role="switch"
          tabIndex={disabled ? -1 : 0}
          aria-checked={isOn}
          aria-label={ariaLabelText}
          aria-describedby={hintText ? `${switchId}-hint` : undefined}
          aria-disabled={disabled}
          className={`switch-button ${isOn ? "on" : "off"} ${
            error ? "input-error" : ""
          } ${disabled ? "disabled" : ""}`}
          onClick={handleToggle}
          onKeyDown={handleToggle}
          style={{
            minWidth: width,
            transition: `background-color ${transitionDuration} ease`,
          }}
          id={switchId}
        >
          <div
            className={`circle ${circleClassName} ${isOn ? "on" : "off"}`}
            aria-hidden="true"
            style={{
              transition: `transform ${transitionDuration} ease, background-color ${transitionDuration} ease`,
            }}
          >
            {isOn
              ? onIcon && <SvgIcn name={onIcon} aria-hidden="true" />
              : offIcon && <SvgIcn name={offIcon} aria-hidden="true" />}
          </div>

          {(onText || offText) && (
            <div
              className={`switch-text ${textClassName} ${isOn ? "on" : "off"}`}
              aria-hidden="true"
            >
              {isOn ? onText : offText}
            </div>
          )}
        </div>
      </div>
    );
  }
);

Switchz.displayName = "Switchz";

export default Switchz;
